import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, MenuProps, Row } from 'antd';
import { CheckOutlined } from '@ant-design/icons';

import styles from './index.module.scss';
import { Order } from '../../../../store/types';
import { useSearchParams } from '../../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../../utils';
import { IconSortMenu } from '../../Icon';
import { CommentOrderColumn } from '../../../../store/comment/list';
interface QueryValues {
  orderBy: Order;
  orderByColumn: CommentOrderColumn;
}

const createKey = ({ orderByColumn, orderBy }: QueryValues): string =>
  `${orderByColumn}_${orderBy}`;
const DEFAULT_ORDER_BY_COLUMN: CommentOrderColumn = 'createdAt';
const DEFAULT_ORDER_BY: Order = 'DESC';

const DEFAULT_KEY = createKey({
  orderByColumn: DEFAULT_ORDER_BY_COLUMN,
  orderBy: DEFAULT_ORDER_BY,
});

const menuData: {
  label: string;
  orderByColumn: CommentOrderColumn;
  orderBy: Order;
}[] = [
  { label: 'Most liked', orderByColumn: 'numberOfLikes', orderBy: 'DESC' },
  { label: 'Less liked', orderByColumn: 'numberOfLikes', orderBy: 'ASC' },
  {
    label: 'Most disliked',
    orderByColumn: 'numberOfDislikes',
    orderBy: 'DESC',
  },
  { label: 'Less disliked', orderByColumn: 'numberOfDislikes', orderBy: 'ASC' },
  { label: 'Newest', orderByColumn: 'createdAt', orderBy: 'DESC' },
  { label: 'Oldest', orderByColumn: 'createdAt', orderBy: 'ASC' },
  {
    label: 'Most commented',
    orderByColumn: 'numberOfComments',
    orderBy: 'DESC',
  },
];

const SortBy = ({ onChange }: { onChange?: (params: QueryValues) => void }) => {
  const [selectedKey, setSelectedKey] = useState<string>(DEFAULT_KEY);
  const { searchParams, replaceSearchParams } = useSearchParams();

  useEffect(() => {
    const { orderBy, orderByColumn } = getValidSearchParams<QueryValues>(
      '*',
      searchParams
    );
    setSelectedKey(
      orderBy && orderByColumn
        ? createKey({ orderBy, orderByColumn })
        : DEFAULT_KEY
    );
  }, [searchParams]);

  const handleMenuClick = (
    orderByColumn: CommentOrderColumn,
    orderBy: Order
  ) => {
    const key = createKey({ orderByColumn, orderBy });
    setSelectedKey(key);
    if (onChange) {
      onChange({ orderByColumn, orderBy });
    } else {
      replaceSearchParams({ orderByColumn, orderBy });
    }
  };

  const sortMenu: MenuProps = useMemo(
    () => ({
      style: { width: 200 },
      items: [
        ...menuData.map(({ label, orderByColumn, orderBy }) => {
          const key = createKey({ orderByColumn, orderBy });

          return {
            key,
            label: (
              <Row justify="space-between" align="middle" gutter={16}>
                {label}{' '}
                {selectedKey === key ? (
                  <CheckOutlined style={{ margin: '0 8px 0 16px' }} />
                ) : null}
              </Row>
            ),
            onClick: () => handleMenuClick(orderByColumn, orderBy),
          };
        }),
      ],
    }),
    [selectedKey]
  );

  return (
    <Dropdown menu={sortMenu} trigger={['click']} placement="bottomRight">
      <Button type="text" className={styles.button}>
        <IconSortMenu /> Sort by
      </Button>
    </Dropdown>
  );
};

export default SortBy;
